import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import { DateTime } from 'luxon';

// * Icons
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import DataObjectRoundedIcon from '@mui/icons-material/DataObjectRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import RouterRoundedIcon from '@mui/icons-material/RouterRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import WorkspacesIcon from '@mui/icons-material/Workspaces';

// * Queries
import {
  useGetEnergyOverviewDataQuery,
  useLazyGetEnergyOverviewDataQuery,
  useLazyGetEnergyConsumptionQuery,
  useLazyGetPowerLoadProfileAnalysisQuery,
  useLazyGetPowerLoadProfileAnalysisSeuQuery,
} from '@/api/energy-analysis/calculateEnergySavingsApiSlice';
import { useLazyGetSystemQoSByIdQuery } from '@/api/energy-analysis/systemQoSApiSlice';
import { useLazyGetBuildingByIdQuery } from '@/api/recalc/endpoints/buildingApiSlice';
import { useLazyGetDataQuery } from '@/api/recalc/endpoints/dataApiSlice';
import { useLazyGetGatewayByIdQuery } from '@/api/recalc/endpoints/gatewayApiSlice';
import { useLazyGetSubscriptionByIdQuery } from '@/api/recalc/endpoints/jobSubscriptionApiSlice';
import { useLazyGetSensorsByIdQuery } from '@/api/recalc/endpoints/sensorsApiSlice';
import { useLazyGetUnitByIdQuery } from '@/api/recalc/endpoints/unitsApiSlice';
import { useLazyGetEnergyGroupByIdQuery } from '@/api/recalcV2/endpoints/energyGroupsApiSlice';
import { useLazyGetEnergyTreeNodeByIdQuery } from '@/api/recalcV2/endpoints/energyTreeNodesApiSlice';

import InsetDrawer from '@/components/drawer/InsetDrawer';
import PowerLoadProfilesAnalysisChart from '@/components/highcharts/powerAnalyser/PowerLoadProfilesAnalysisChart';
import DistributionAlarmBarChart from '@/features/alarm-hub/components/highcharts/DistributionAlarmBarChart';
import DistributionAlarmChart from '@/features/alarm-hub/components/highcharts/DistributionAlarmChart';
import UnitAlarmChart from '@/features/alarm-hub/components/highcharts/UnitAlarmChart';
import { getTypeColor, getTypeIcon } from '@/utils/nodeTypeMapper'; // Adjust the import path as necessary
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocation, useNavigate } from 'react-router-dom';

const formatAndEncodeTimestampForAPI = (date) => {
  const isoString = DateTime.fromJSDate(date, { zone: 'utc' }).toFormat(
    "yyyy-MM-dd'T'HH:mm:ss'Z'",
  );
  return encodeURIComponent(isoString);
};

function formatDanishDate(timestamp) {
  if (!timestamp) return '';
  return DateTime.fromISO(timestamp, { zone: 'utc' })
    .setLocale('da')
    .toFormat('dd. MMM yyyy HH:mm:ss');
}

const toUTC = (timestamp) =>
  DateTime.fromISO(timestamp, { zone: 'utc' }).toMillis();

const SEVERITY_COLORS = {
  Critical: '#c72929',
  Major: '#c72929',
  Medium: '#f47f1f',
  Minor: '#ffb300',
  Trivial: '#4cae4f',
  Information: '#1975d2',
  Unknown: 'transparent',
};

const SEVERITY_BUTTON_CONFIG = {
  Critical: {
    label: 'Critical',
    icon: <ErrorRoundedIcon />,
    color: SEVERITY_COLORS.Critical,
  },
  Major: {
    label: 'Major',
    icon: <ErrorRoundedIcon />,
    color: SEVERITY_COLORS.Major,
  },
  Medium: {
    label: 'Medium',
    icon: <WarningRoundedIcon />,
    color: SEVERITY_COLORS.Medium,
  },
  Minor: {
    label: 'Minor',
    icon: <WarningRoundedIcon />,
    color: SEVERITY_COLORS.Minor,
  },
  Information: {
    label: 'Information',
    icon: <InfoRoundedIcon />,
    color: SEVERITY_COLORS.Information,
  },
  Trivial: {
    label: 'Trivial',
    icon: <InfoRoundedIcon />,
    color: SEVERITY_COLORS.Trivial,
  },
};

const getAlertSeverity = (severity) => {
  switch (severity) {
    case 'Critical':
      return 'error';
    case 'Major':
      return 'error';
    case 'Medium':
      return 'warning';
    case 'Minor':
      return 'warning';
    case 'Trivial':
      return 'success';
    case 'Information':
      return 'info';
    default:
      return 'default';
  }
};

function EntityListSection({ title, items = [], renderItem }) {
  if (!items?.length) return null;
  return (
    <Box>
      <List
        dense
        disablePadding
        subheader={
          <ListSubheader
            component="div"
            sx={{
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? '#313131ff' : '#f5f5f5',
              color: 'text.primary',
              fontWeight: 'bold',
            }}
          >
            {title}
          </ListSubheader>
        }
      >
        {items.map((item, idx) => (
          <Box key={idx}>
            {renderItem(item)}
            {idx < items.length - 1 && <Divider component="li" />}
          </Box>
        ))}
      </List>
    </Box>
  );
}

const AlarmDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const handleBack = React.useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const alarm = location.state?.alarm;

  const [associationContent, setAssociationContent] = useState(null);
  const [powerLoadProfileData, setPowerLoadProfileData] = useState(null);
  const [selectedEnergyTreeNode, setSelectedEnergyTreeNode] = useState(null);
  const [selectedSEUType, setSelectedSEUType] = useState(null);
  const [mode, setMode] = useState('node');
  const [seuOptions, setSeuOptions] = useState([]);
  const [distributionBoards, setDistributionBoards] = useState([]);
  const [distributionBoardData, setDistributionBoardData] = useState(null);
  const [distributionData, setDistributionData] = useState(null);
  const [alarmEnergyGroupId, setAlarmEnergyGroupId] = useState(null);
  const [open, setOpen] = useState(false);

  const [getUnitById] = useLazyGetUnitByIdQuery();
  const [getSensorsById] = useLazyGetSensorsByIdQuery();
  const [getSubscriptionById] = useLazyGetSubscriptionByIdQuery();
  const [getBuildingById] = useLazyGetBuildingByIdQuery();
  const [getGatewayById] = useLazyGetGatewayByIdQuery();
  const [getEnergyGroupById] = useLazyGetEnergyGroupByIdQuery();
  const [getEnergyTreeNodeById] = useLazyGetEnergyTreeNodeByIdQuery();
  const [getSystemQoSById] = useLazyGetSystemQoSByIdQuery();
  const [getPowerLoadProfileAnalysis] =
    useLazyGetPowerLoadProfileAnalysisQuery();
  const [getPowerLoadProfileAnalysisSeu] =
    useLazyGetPowerLoadProfileAnalysisSeuQuery();
  const [getEnergyConsumption] = useLazyGetEnergyConsumptionQuery();
  const [getEnergyOverviewData] = useLazyGetEnergyOverviewDataQuery();

  const [getData, { isLoading: isLoadingData, isFetching: isFetchingData }] =
    useLazyGetDataQuery();

  const from = DateTime.fromISO(alarm.DataFrom).minus({ hours: 12 }).toMillis();
  const to = DateTime.fromISO(alarm.DataTo).plus({ hours: 12 }).toMillis();

  useEffect(() => {
    if (!alarm?.Associations) return;

    const {
      Units,
      Sensors,
      Buildings,
      JobSubscriptions,
      Gateways,
      EnergyGroups,
      EnergyTreeNodes,
    } = alarm.Associations;

    const fetchAssociations = async () => {
      try {
        if (Units.length > 0) {
          const units = await Promise.all(
            Units.map((id) => getUnitById(id).unwrap()),
          );

          if (alarm?.DataFrom && alarm?.DataTo) {
            const from = DateTime.fromISO(alarm?.DataFrom)
              .minus({ hours: 12 })
              .toMillis();
            const to = DateTime.fromISO(alarm?.DataTo)
              .plus({ hours: 12 })
              .toMillis();

            const seriesPerUnit = {};

            await Promise.all(
              units.map(async (unit) => {
                const sensorSeries = await Promise.all(
                  (unit.UnitSensors ?? []).map(async (unitSensor) => {
                    if (!unitSensor.Sensor || !unitSensor.UnitTypeInput) {
                      console.warn(
                        '❌ Skipping sensor due to missing fields:',
                        {
                          unitSensorId: unitSensor.UnitSensorId,
                          hasSensor: !!unitSensor.Sensor,
                          hasUnitTypeInput: !!unitSensor.UnitTypeInput,
                        },
                      );
                      return null;
                    }

                    try {
                      const fetchedData = await getData({
                        from: formatAndEncodeTimestampForAPI(new Date(from)),
                        to: formatAndEncodeTimestampForAPI(new Date(to)),
                        unitId: unit.UnitId,
                        datatype: 'temperature',
                        unitTypeInputId:
                          unitSensor.UnitTypeInput.UnitTypeInputId,
                        aggregateType: 'hour',
                      }).unwrap(); // 👈 unwrap the promise returned by the trigger

                      if (!fetchedData || fetchedData.length === 0) {
                        console.warn(
                          '⚠️ No data returned for',
                          unit.UnitId,
                          unitSensor.SensorId,
                        );
                        return null;
                      }

                      return {
                        name: `${unitSensor?.CustomName || 'Sensor'} (ID: ${unit.UnitId})`,
                        base: fetchedData[0]?.Base,
                        data: fetchedData.map((item) => [
                          toUTC(item.Timestamp),
                          parseFloat(item.Value.toFixed(2)),
                          item.Base,
                        ]),
                        type: 'spline',
                        showInNavigator: true,
                      };
                    } catch (e) {
                      console.error(
                        `❌ Failed fetching data for Unit ${unit.UnitId}, Sensor ${unitSensor.SensorId}`,
                        e,
                      );
                      return null;
                    }
                  }),
                );

                const filteredSeries = sensorSeries.filter(Boolean);
                seriesPerUnit[unit.UnitId] = filteredSeries;
              }),
            );

            const unitsWithData = units.map((unit) => ({
              ...unit,
              Data: seriesPerUnit[unit.UnitId] || [],
            }));

            setAssociationContent((prev) => ({
              ...prev,
              Units: unitsWithData,
            }));
          } else {
            setAssociationContent((prev) => ({
              ...prev,
              Units: units,
            }));
          }
        } else {
          setAssociationContent((prev) => ({
            ...prev,
            Units: [],
          }));
        }

        if (Sensors.length > 0) {
          const sensors = await Promise.all(
            Sensors.map((id) => getSensorsById(id).unwrap()),
          );
          setAssociationContent((prev) => ({
            ...prev,
            Sensors: sensors,
          }));
        } else {
          setAssociationContent((prev) => ({
            ...prev,
            Sensors: [],
          }));
        }

        if (Buildings.length > 0) {
          const buildings = await Promise.all(
            Buildings.map((id) => getBuildingById(id).unwrap()),
          );
          setAssociationContent((prev) => ({
            ...prev,
            Buildings: buildings,
          }));
        } else {
          setAssociationContent((prev) => ({
            ...prev,
            Buildings: [],
          }));
        }

        if (JobSubscriptions.length > 0) {
          const subscriptions = await Promise.all(
            JobSubscriptions.map((id) => getSubscriptionById({ id }).unwrap()),
          );
          setAssociationContent((prev) => ({
            ...prev,
            JobSubscriptions: subscriptions,
          }));
        } else {
          setAssociationContent((prev) => ({
            ...prev,
            JobSubscriptions: [],
          }));
        }

        if (Gateways.length > 0) {
          const gateways = await Promise.all(
            Gateways.map((id) => getGatewayById(id).unwrap()),
          );
          setAssociationContent((prev) => ({
            ...prev,
            Gateways: gateways,
          }));
        } else {
          setAssociationContent((prev) => ({
            ...prev,
            Gateways: [],
          }));
        }

        if (EnergyGroups.length > 0) {
          const energyGroups = await Promise.all(
            EnergyGroups.map((id) =>
              getEnergyGroupById({
                energyGroupId: id,
                accountId: alarm.AccountId,
              }).unwrap(),
            ),
          );

          const overview = await Promise.all(
            EnergyGroups.map((group) =>
              getEnergyOverviewData({
                accountId: alarm.AccountId,
                energyGroupId: group,
                start: from,
                end: to,
              }).unwrap(),
            ),
          );

          setDistributionData(overview?.[0].EnergyDistributionData);

          setAssociationContent((prev) => ({
            ...prev,
            EnergyGroups: energyGroups,
          }));
        }

        if (EnergyTreeNodes.length > 0) {
          const energyTreeNode = await Promise.all(
            EnergyTreeNodes.map((node) =>
              getEnergyTreeNodeById({
                energyTreeNodeId: node,
                accountId: alarm.AccountId,
              }).unwrap(),
            ),
          );
          setAssociationContent((prev) => ({
            ...prev,
            EnergyTreeNodes: energyTreeNode,
          }));
        }
      } catch (error) {
        console.error('Error fetching associations:', error);
      }
    };

    const hasAnyAssociations =
      Units?.length > 0 ||
      Sensors?.length > 0 ||
      Buildings?.length > 0 ||
      JobSubscriptions?.length > 0 ||
      Gateways?.length > 0 ||
      EnergyGroups?.length > 0 ||
      EnergyTreeNodes?.length > 0;

    if (hasAnyAssociations) {
      fetchAssociations();
    }
  }, [
    alarm,
    from,
    getBuildingById,
    getData,
    getEnergyGroupById,
    getEnergyOverviewData,
    getEnergyTreeNodeById,
    getGatewayById,
    getSensorsById,
    getSubscriptionById,
    getSystemQoSById,
    getUnitById,
    to,
  ]);

  useEffect(() => {
    if (!alarm || !alarm.DataFrom || !alarm.DataTo) return;
    if (!distributionBoards?.length) return;

    let cancelled = false;

    (async () => {
      try {
        const from = DateTime.fromISO(alarm.DataFrom)
          .minus({ hours: 12 })
          .toMillis();
        const to = DateTime.fromISO(alarm.DataTo)
          .plus({ hours: 12 })
          .toMillis();

        const results = await Promise.all(
          distributionBoards.map((boardId) =>
            getEnergyConsumption({
              accountId: alarm.AccountId,
              energyGroupId: alarmEnergyGroupId,
              nodeId: boardId,
              start: from,
              end: to,
              aggregate: 'day',
            }).unwrap(),
          ),
        );

        if (cancelled) return;
        setDistributionBoardData(results);
      } catch (err) {
        if (!cancelled)
          console.error('Failed to fetch energy consumption:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [distributionBoards, alarmEnergyGroupId, getEnergyConsumption, alarm]);

  useEffect(() => {
    if (
      alarm?.DataFrom &&
      alarm?.DataTo &&
      mode === 'node' &&
      selectedEnergyTreeNode
    ) {
      const from = DateTime.fromISO(alarm.DataFrom)
        .minus({ hours: 12 })
        .toMillis();
      const to = DateTime.fromISO(alarm.DataTo).plus({ hours: 12 }).toMillis();
      getPowerLoadProfileAnalysis({
        nodeId: selectedEnergyTreeNode.energyTreeNodeId,
        energyGroupId: selectedEnergyTreeNode.energyGroupId,
        accountId: alarm.AccountId,
        startTimestamp: from,
        endTimestamp: to,
      })
        .unwrap()
        .then((data) => {
          setPowerLoadProfileData(data);
        })
        .catch((error) => {
          console.error('Error fetching Power Load Profile Analysis:', error);
        });
    } else if (
      alarm?.DataFrom &&
      alarm?.DataTo &&
      mode === 'seu' &&
      selectedSEUType
    ) {
      const from = DateTime.fromISO(alarm.DataFrom)
        .minus({ hours: 12 })
        .toMillis();
      const to = DateTime.fromISO(alarm.DataTo).plus({ hours: 12 }).toMillis();
      getPowerLoadProfileAnalysisSeu({
        seuType: selectedSEUType,
        energyGroupId: alarmEnergyGroupId,
        accountId: alarm.AccountId,
        startTimestamp: from,
        endTimestamp: to,
      })
        .unwrap()
        .then((data) => {
          setPowerLoadProfileData(data);
        })
        .catch((error) => {
          console.error(
            'Error fetching Power Load Profile Analysis for SEU:',
            error,
          );
        });
    }

    if (alarm && alarm?.MetaData)
      if (alarm?.MetaData) {
        try {
          const meta = JSON.parse(alarm.MetaData);

          const energyGroupId = meta.energy_tree_id;
          setAlarmEnergyGroupId(energyGroupId);

          const seuTypes =
            meta.alarm_details
              ?.filter(
                (detail) => detail.alarm_scope === 'SEU' && detail.seu_type,
              )
              .map((detail) => detail.seu_type) ?? [];

          const distributionBoards =
            meta.alarm_details
              ?.filter(
                (detail) =>
                  detail.alarm_scope === 'distribution_board' &&
                  detail.distribution_board_id,
              )
              .map((detail) => detail.distribution_board_id) ?? [];

          setDistributionBoards(distributionBoards);
          setSeuOptions(seuTypes);
        } catch (err) {
          console.error('Failed to parse alarm.MetaData:', err);
        }
      }
  }, [
    alarm,
    alarmEnergyGroupId,
    getPowerLoadProfileAnalysis,
    getPowerLoadProfileAnalysisSeu,
    mode,
    selectedEnergyTreeNode,
    selectedSEUType,
  ]);

  const handleSelectSEUType = (event, newValue) => {
    setSelectedSEUType(newValue);
    setMode('seu');
  };

  const handleSelectEnergyTreeNode = (event, newValue) => {
    setSelectedEnergyTreeNode(newValue);
    setMode('node');
  };

  if (!alarm) return null;
  return (
    <Container maxWidth="xxl" sx={{ mb: 4 }}>
      <Stack direction="row" spacing={2} justifyContent="space-between" my={1}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon className="arrow-icon" />}
          onClick={handleBack}
          sx={{
            fontSize: '16px',
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'transparent', // remove hover highlight
              color: 'primary.main',
              transition: 'color 0.3s',
            },
            '&:hover .arrow-icon': {
              transform: 'translateX(-4px)',
              color: 'primary.main',
              transition: 'transform 0.3s, color 0.3s, translateX 0.3s',
            },
          }}
        >
          Go back
        </Button>

        <Button
          variant="text"
          startIcon={<DataObjectRoundedIcon />}
          onClick={() => setOpen(true)}
          sx={{
            fontSize: '16px',
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'transparent', // remove hover highlight
              color: 'primary.main',
              transition: 'color 0.3s',
            },
          }}
        >
          View Meta Data
        </Button>
      </Stack>
      <Alert
        variant="standard"
        severity={getAlertSeverity(alarm.Severity)}
        icon={SEVERITY_BUTTON_CONFIG[alarm.Severity]?.icon || null}
        sx={{
          borderLeft: '6px solid',
          borderLeftColor: SEVERITY_COLORS[alarm.Severity] || 'divider',
          my: 1,
        }}
      >
        <AlertTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1, // 🔑 make it stretch inside MuiAlert-message
          }}
        >
          <Box display="flex" alignItems="center" gap={1} flex={1}>
            {alarm?.AlarmType}
            <Box display="flex" alignItems="center" gap={1}>
              <AccessTimeRoundedIcon fontSize="small" />
              {formatDanishDate(alarm?.Created)}
            </Box>
            <ArrowForwardRoundedIcon fontSize="small" /> {alarm?.AlarmSubtype}
          </Box>
        </AlertTitle>

        <Typography variant="caption" color="text.primary">
          {alarm.Message ? `Message: ${alarm.Message}` : ''}
        </Typography>
        <br />
        <Typography variant="caption" color="text.primary">
          {alarm.Details ? `Details: ${alarm.Details}` : ''}
        </Typography>
      </Alert>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card
            variant="outlined"
            sx={{ maxHeight: '65vh', overflowY: 'auto' }}
          >
            <EntityListSection
              title="Buildings"
              items={associationContent?.Buildings}
              renderItem={(building) => (
                <ListItemButton>
                  <ListItemIcon>
                    <HomeWorkRoundedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={building.Name || 'Unknown'}
                    secondary={
                      <>
                        Building ID: {building.BuildingId}
                        {building?.Location?.Address
                          ? ` • ${building.Location.Address}`
                          : ''}
                      </>
                    }
                    primaryTypographyProps={{ variant: 'subtitle1' }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      color: 'text.secondary',
                    }}
                  />
                </ListItemButton>
              )}
            />

            <EntityListSection
              title="Energy Groups"
              items={associationContent?.EnergyGroups}
              renderItem={(group) => (
                <ListItemButton>
                  <ListItemIcon>
                    <WorkspacesIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={group.name || 'Unknown'}
                    secondary={
                      group?.options?.state
                        ? `State: ${group.options.state}`
                        : undefined
                    }
                    primaryTypographyProps={{ variant: 'subtitle1' }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      color: 'text.secondary',
                    }}
                  />
                </ListItemButton>
              )}
            />

            <EntityListSection
              title="SEU Types"
              items={seuOptions}
              renderItem={(type) => (
                <ListItemButton
                  // disabled={alarm.Subtype !== 'PowerDistributionAnomaly'}
                  onClick={(event) => handleSelectSEUType(event, type)}
                  sx={{ minHeight: 56 }}
                >
                  <ListItemIcon>
                    <div
                      style={{
                        backgroundColor: getTypeColor(type),
                        height: 30,
                        width: 30,
                        borderRadius: '50%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: '#fff',
                      }}
                    >
                      <i className={`fa-solid ${getTypeIcon(type)}`}></i>
                    </div>
                  </ListItemIcon>
                  <ListItemText
                    primary={type || 'Unknown'}
                    // secondary={`Type ID: ${type.TypeId}`}
                    primaryTypographyProps={{ variant: 'subtitle1' }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      color: 'text.secondary',
                    }}
                  />
                </ListItemButton>
              )}
            />

            <EntityListSection
              title="Energy Tree Nodes"
              items={associationContent?.EnergyTreeNodes}
              renderItem={(node) => (
                <ListItemButton
                  onClick={(event) => handleSelectEnergyTreeNode(event, node)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor:
                      selectedEnergyTreeNode?.energyTreeNodeId ===
                      node.energyTreeNodeId
                        ? 'action.selected'
                        : 'inherit',
                  }}
                >
                  <ListItemIcon>
                    <div
                      style={{
                        backgroundColor: getTypeColor(node.type),
                        height: 30,
                        width: 30,
                        borderRadius: '50%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: '#fff',
                      }}
                    >
                      <i className={`fa-solid ${getTypeIcon(node.type)}`}></i>
                    </div>
                  </ListItemIcon>
                  <ListItemText
                    primary={node.name || 'Unknown'}
                    secondary={`${node.energyTreeNodeId}`}
                    primaryTypographyProps={{ variant: 'subtitle1' }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      color: 'text.secondary',
                    }}
                  />
                </ListItemButton>
              )}
            />

            <EntityListSection
              title="Job Subscriptions"
              items={associationContent?.JobSubscriptions}
              renderItem={(job) => (
                <Tooltip title={job.JobType || 'No description available'}>
                  <ListItemButton>
                    <ListItemIcon>
                      <ReceiptLongRoundedIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={job.DisplayName || job.JobType || 'Unknown'}
                      secondary={`JobSubscription ID: ${job.JobSubscriptionId}`}
                      primaryTypographyProps={{ variant: 'subtitle1' }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        color: 'text.secondary',
                      }}
                    />
                    {job.JobType && <Chip size="small" label={job.JobType} />}
                  </ListItemButton>
                </Tooltip>
              )}
            />

            <EntityListSection
              title="Gateways"
              items={associationContent?.Gateways}
              renderItem={(gateway) => (
                <ListItemButton>
                  <ListItemIcon>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '40%',
                        bgcolor: '#323232',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          border: '1.5px solid #fff',
                        }}
                      />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={gateway.Name || 'Unknown'}
                    secondary={
                      <>
                        Gateway ID: {gateway.GatewayId} • Online:{' '}
                        {gateway.Online ? 'Yes' : 'No'}
                        {gateway.LastActivity
                          ? ` • Last: ${formatDanishDate(gateway.LastActivity)}`
                          : ''}
                        {gateway.CurrentApplicationFirmware
                          ? ` • FW: ${gateway.CurrentApplicationFirmware}`
                          : ''}
                      </>
                    }
                    primaryTypographyProps={{ variant: 'subtitle1' }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      color: 'text.secondary',
                    }}
                  />
                </ListItemButton>
              )}
            />

            <EntityListSection
              title="Sensors"
              items={associationContent?.Sensors}
              renderItem={(sensor) => (
                <ListItemButton>
                  <ListItemIcon>
                    <SpeedRoundedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={sensor.SensorType || 'Unknown Sensor Type'}
                    secondary={
                      <>
                        ID: {sensor.SensorId}
                        {sensor.DataLastRecorded
                          ? ` • Last data: ${formatDanishDate(sensor.DataLastRecorded)}`
                          : ''}
                        {sensor.PairedGatewayId
                          ? ` • Gateway: ${sensor.PairedGatewayId}`
                          : ''}
                        {sensor.DataInterval
                          ? ` • Interval: ${sensor.DataInterval}`
                          : ''}
                        {sensor.CurrentApplicationFirmware
                          ? ` • FW: ${sensor.CurrentApplicationFirmware}`
                          : ''}
                      </>
                    }
                    primaryTypographyProps={{ variant: 'subtitle1' }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      color: 'text.secondary',
                    }}
                  />
                </ListItemButton>
              )}
            />

            <EntityListSection
              title="Units"
              items={associationContent?.Units}
              renderItem={(unit) => (
                <ListItemButton>
                  <ListItemIcon>
                    <RouterRoundedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={unit?.UnitType?.Name || 'Unknown'}
                    secondary={`Unit ID: ${unit.UnitId}${unit.Name ? ` • Name: ${unit.Name}` : ''}`}
                    primaryTypographyProps={{ variant: 'subtitle1' }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      color: 'text.secondary',
                    }}
                  />
                </ListItemButton>
              )}
            />
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card variant="outlined">
            <Stack direction="row" justifyContent="space-between" px={2} pt={2}>
              <CardHeader
                title={
                  mode === 'node'
                    ? selectedEnergyTreeNode?.name
                      ? `Node: ${selectedEnergyTreeNode.name}`
                      : ''
                    : selectedSEUType
                      ? `SEU Type: ${selectedSEUType}`
                      : ''
                }
              />
              {alarm?.DataFrom && alarm?.DataTo && (
                <Box display="flex" alignItems="center" gap={1}>
                  <AccessTimeRoundedIcon />
                  {formatDanishDate(alarm?.DataFrom)} -{' '}
                  {formatDanishDate(alarm?.DataTo)}
                </Box>
              )}
            </Stack>
            <CardContent>
              {!powerLoadProfileData &&
                !alarm.AlarmSubtype.includes('Distribution') &&
                !alarm.AlarmSubtype.includes('BaseloadDeviation') && (
                  <UnitAlarmChart
                    alarm={alarm}
                    unitData={associationContent?.Units?.[0]?.Data || []}
                    isLoading={isLoadingData || isFetchingData}
                  />
                )}
              {alarm.AlarmSubtype.includes('BaseloadDeviation') &&
                powerLoadProfileData && (
                  <PowerLoadProfilesAnalysisChart
                    powerData={powerLoadProfileData.PowerData}
                    loadData={powerLoadProfileData.PowerLoadProfileData}
                    isLoading={false}
                  />
                )}

              {distributionBoardData &&
                distributionBoardData.length > 0 &&
                !alarm.AlarmSubtype.includes('BaseloadDeviation') && (
                  <DistributionAlarmBarChart
                    items={distributionBoardData || []}
                  />
                )}
              {alarm.AlarmSubtype.includes('Distribution') && (
                <DistributionAlarmChart data={distributionData || []} />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <InsetDrawer
        title="Alarm Meta Data"
        open={open}
        onClose={() => setOpen(false)}
        content={
          alarm?.MetaData && (
            <Box height="85vh" overflow="auto" width="100%">
              <pre
                style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  backgroundColor: (theme) => theme.palette.background.paper,
                  borderRadius: '4px',
                  overflowX: 'auto',
                }}
              >
                {JSON.stringify(JSON.parse(alarm.MetaData), null, 2)}
              </pre>
            </Box>
          )
        }
      />
    </Container>
  );
};

export default AlarmDetailPage;
